import { RefObject, useEffect, useRef } from 'react';

interface CanvasSize {
  width: number;
  height: number;
}

interface CanvasRenderer {
  setup(ctx: CanvasRenderingContext2D, size: CanvasSize): void;
  draw(ctx: CanvasRenderingContext2D, size: CanvasSize, deltaMs: number): void;
  drawStatic(ctx: CanvasRenderingContext2D, size: CanvasSize): void;
}

const DPR_LIMIT = 1.5;
const FRAME_INTERVAL_MS = {
  normal: 1000 / 18,
  low: 1000 / 8,
} as const;

const getParentSize = (canvas: HTMLCanvasElement): CanvasSize => {
  const bounds = canvas.parentElement?.getBoundingClientRect();

  return {
    width: Math.max(1, Math.floor(bounds?.width || window.innerWidth)),
    height: Math.max(1, Math.floor(bounds?.height || window.innerHeight)),
  };
};

export function useCanvasLoop(
  canvasRef: RefObject<HTMLCanvasElement>,
  renderer: CanvasRenderer,
  activity: 'normal' | 'low'
): void {
  const rendererRef = useRef(renderer);
  const activityRef = useRef(activity);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const sizeRef = useRef<CanvasSize | null>(null);

  useEffect(() => {
    rendererRef.current = renderer;
    if (contextRef.current && sizeRef.current) {
      rendererRef.current.setup(contextRef.current, sizeRef.current);
    }
  }, [renderer]);

  useEffect(() => {
    activityRef.current = activity;
  }, [activity]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    contextRef.current = ctx;
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let animationFrameId = 0;
    let lastFrameTime = 0;
    let lastDrawTime = 0;
    let size = getParentSize(canvas);
    let isReducedMotion = reducedMotionQuery.matches;
    let isVisible = document.visibilityState !== 'hidden';

    const applyCanvasSize = () => {
      const pixelRatio = Math.min(window.devicePixelRatio || 1, DPR_LIMIT);
      const nextSize = getParentSize(canvas);
      size = nextSize;
      sizeRef.current = nextSize;
      canvas.width = Math.floor(nextSize.width * pixelRatio);
      canvas.height = Math.floor(nextSize.height * pixelRatio);
      canvas.style.width = `${nextSize.width}px`;
      canvas.style.height = `${nextSize.height}px`;
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      rendererRef.current.setup(ctx, nextSize);
    };

    const cancelLoop = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
        animationFrameId = 0;
      }
    };

    const drawStatic = () => {
      cancelLoop();
      rendererRef.current.drawStatic(ctx, size);
    };

    const draw = (timestamp: number) => {
      if (!isVisible || isReducedMotion) {
        animationFrameId = 0;
        return;
      }

      animationFrameId = window.requestAnimationFrame(draw);
      const interval = FRAME_INTERVAL_MS[activityRef.current];

      if (timestamp - lastFrameTime < interval) {
        return;
      }

      const deltaMs = lastDrawTime ? timestamp - lastDrawTime : interval;
      lastFrameTime = timestamp;
      lastDrawTime = timestamp;
      rendererRef.current.draw(ctx, size, deltaMs);
    };

    const start = () => {
      cancelLoop();
      lastFrameTime = 0;
      lastDrawTime = 0;

      if (!isVisible) {
        return;
      }

      if (isReducedMotion) {
        drawStatic();
        return;
      }

      animationFrameId = window.requestAnimationFrame(draw);
    };

    const reset = () => {
      applyCanvasSize();
      start();
    };

    const handleVisibilityChange = () => {
      isVisible = document.visibilityState !== 'hidden';
      start();
    };

    const handleReducedMotionChange = () => {
      isReducedMotion = reducedMotionQuery.matches;
      start();
    };

    reset();

    const resizeObserver = new ResizeObserver(reset);
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    reducedMotionQuery.addEventListener('change', handleReducedMotionChange);

    return () => {
      cancelLoop();
      resizeObserver.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      reducedMotionQuery.removeEventListener('change', handleReducedMotionChange);
      contextRef.current = null;
      sizeRef.current = null;
    };
  }, [canvasRef]);
}
